import { useCallback, useEffect, useRef, useState } from 'react'
import type { ISODate } from '@app/shared'

import { isDemoMode, useUploadVoiceMemo } from '../../lib/data'

// ============================================================================
// HOLD-TO-RECORD — the phone's half of the voice-memo path (1d).
//
// THE UPLOAD PATH IS THE DESKTOP'S, EXACTLY. `useUploadVoiceMemo()` is the same
// mutation components/today/VoiceRecorder.tsx calls, which is the same
// `POST /api/audio/uploads` → `PUT uploadUrl` two-call contract
// (apps/worker/src/routes/audio.ts). There is no second endpoint, no second
// content type and no re-encode: whatever MediaRecorder produced goes up
// verbatim, because a conversion between the microphone and the transcript is a
// lossy step nobody can point at afterwards.
//
// WHAT IS GENUINELY DIFFERENT, and why this is a hook rather than a call into
// that component:
//   · The GESTURE. Desktop is tap-to-start / tap-to-stop; the phone records for
//     as long as the finger is down (README §Interactions → Capture). A release
//     is a stop, and a pointer the browser cancels is ALSO a stop — an open
//     microphone surviving a cancelled gesture is the one failure this control
//     is not allowed to have.
//   · The METER. Desktop draws six bars off one RMS number; 1d draws twenty bars
//     off the spectrum, "animate from real input levels". A single RMS fanned
//     out across twenty bars would be twenty copies of one number wearing a
//     disguise, which is a decoration on a timer — the thing the desktop
//     recorder's own comments refuse.
//
// It duplicates the getUserMedia / MIME-picking preamble rather than importing
// it, because that preamble currently lives inside the desktop component's body.
// Flagged in the port report as the one thing here worth converging later; the
// two must not drift, and neither invents a state the other does not have.
// ============================================================================

/** Ten minutes, the same cap the desktop recorder enforces for the same reason. */
const MAX_SECONDS = 600

/** In preference order — Opus in WebM everywhere that matters, `audio/mp4` for Safari. */
const MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/ogg;codecs=opus',
]

/** The mockup draws twenty bars. They are frequency bands, not twenty copies of one level. */
export const WAVEFORM_BARS = 20

export type RecorderPhase = 'idle' | 'starting' | 'recording' | 'uploading' | 'saved' | 'failed'

export interface MemoRecorder {
  phase: RecorderPhase
  /** Seconds, from the wall clock rather than a tick count. */
  elapsed: number
  /** One 0–1 level per bar, loudest-band-first ordering left as the analyser gives it. */
  levels: number[]
  /** The reason, in the browser's own terms, when something refused. */
  problem: string | null
  start: () => void
  /** Stop and send. What a release does. */
  stopAndSave: () => void
  /** Stop and throw the audio away. What Undo does — and it says so before it does it. */
  discard: () => void
  reset: () => void
}

function pickMimeType(): string | null {
  if (typeof MediaRecorder === 'undefined') return null
  const supported = MediaRecorder.isTypeSupported
  if (typeof supported !== 'function') return null
  return MIME_CANDIDATES.find((type) => supported.call(MediaRecorder, type)) ?? null
}

/**
 * How to try again, in the words of the gesture that started this memo: the composer's circle is
 * held ("hold again"); the + menu's Voice memo is picked, then stopped with a tap (V10 board 03).
 */
export function retryPhrase(held: boolean): string {
  return held ? 'hold again' : 'pick Voice memo from the + menu again'
}

export function useMemoRecorder(date: ISODate | undefined, held = true): MemoRecorder {
  const upload = useUploadVoiceMemo()
  // Read when a problem is worded, not when the callbacks were made.
  const retry = useRef(retryPhrase(held))
  retry.current = retryPhrase(held)

  const [phase, setPhase] = useState<RecorderPhase>('idle')
  const [elapsed, setElapsed] = useState(0)
  const [levels, setLevels] = useState<number[]>(() => new Array(WAVEFORM_BARS).fill(0))
  const [problem, setProblem] = useState<string | null>(null)

  const recorder = useRef<MediaRecorder | null>(null)
  const stream = useRef<MediaStream | null>(null)
  const audioCtx = useRef<AudioContext | null>(null)
  const frame = useRef<number | null>(null)
  const ticker = useRef<ReturnType<typeof setInterval> | null>(null)
  const startedAt = useRef(0)
  const chunks = useRef<Blob[]>([])
  /** Set while stopping, so `onstop` knows whether to upload or to bin the blob. */
  const intent = useRef<'save' | 'discard'>('save')

  const releaseHardware = useCallback(() => {
    if (frame.current !== null) cancelAnimationFrame(frame.current)
    frame.current = null
    if (ticker.current) clearInterval(ticker.current)
    ticker.current = null
    // The browser's microphone indicator stays lit until every track is stopped,
    // and a recorder that leaves it on looks exactly like one still listening.
    stream.current?.getTracks().forEach((track) => track.stop())
    stream.current = null
    void audioCtx.current?.close().catch(() => {})
    audioCtx.current = null
    recorder.current = null
    setLevels(new Array(WAVEFORM_BARS).fill(0))
  }, [])

  useEffect(() => releaseHardware, [releaseHardware])

  const finish = useCallback(
    (blob: Blob, durationSec: number) => {
      if (!date) {
        setPhase('failed')
        setProblem('There is no day to save this into yet — the app clock had not answered.')
        return
      }
      setPhase('uploading')
      upload.mutate(
        { date, blob, durationSec },
        {
          onSuccess: () => {
            setPhase('saved')
            setElapsed(0)
          },
          onError: (error) => {
            setPhase('failed')
            setProblem(
              error instanceof Error
                ? error.message
                : 'The memo was recorded but the upload did not go through.',
            )
          },
        },
      )
    },
    [date, upload],
  )

  const stopWith = useCallback(
    (how: 'save' | 'discard') => {
      const active = recorder.current
      if (!active || active.state === 'inactive') {
        releaseHardware()
        if (how === 'discard') {
          setPhase('idle')
          setElapsed(0)
        }
        return
      }
      intent.current = how
      const durationSec = Math.max(1, Math.round((performance.now() - startedAt.current) / 1000))
      active.onstop = () => {
        const blob = new Blob(chunks.current, { type: active.mimeType || 'audio/webm' })
        chunks.current = []
        releaseHardware()
        if (intent.current === 'discard') {
          setPhase('idle')
          setElapsed(0)
          return
        }
        if (blob.size === 0) {
          setPhase('failed')
          setProblem(
            `The recording came back empty, so nothing was uploaded. Check that the microphone is not in use by something else, and ${retry.current}.`,
          )
          return
        }
        finish(blob, durationSec)
      }
      active.stop()
    },
    [finish, releaseHardware],
  )

  const start = useCallback(() => {
    setProblem(null)

    if (isDemoMode) {
      // A memo's life is R2 plus a GPU on a desktop; a fixture has neither, and
      // accepting audio it would have to throw away is worse than refusing.
      setPhase('failed')
      setProblem(
        'Demo mode has no microphone path: a memo is uploaded to R2 and transcribed on the desktop PC, and neither exists without an account. The queue below is sample data.',
      )
      return
    }

    const mimeType = pickMimeType()
    if (!navigator.mediaDevices?.getUserMedia || !mimeType) {
      setPhase('failed')
      setProblem('This browser cannot record audio, so nothing was recorded.')
      return
    }

    setPhase('starting')
    void navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((media) => {
        stream.current = media
        chunks.current = []
        const active = new MediaRecorder(media, { mimeType })
        active.ondataavailable = (event) => {
          if (event.data.size > 0) chunks.current.push(event.data)
        }
        recorder.current = active
        startedAt.current = performance.now()
        setElapsed(0)
        setPhase('recording')
        active.start()

        // The elapsed clock reads the WALL CLOCK rather than counting ticks: a
        // throttled tab would otherwise show a timer that disagrees with the
        // audio it is timing. It also enforces the cap.
        ticker.current = setInterval(() => {
          const seconds = (performance.now() - startedAt.current) / 1000
          setElapsed(seconds)
          if (seconds >= MAX_SECONDS) stopWith('save')
        }, 200)

        try {
          const ctx = new AudioContext()
          audioCtx.current = ctx
          const analyser = ctx.createAnalyser()
          analyser.fftSize = 128 // 64 bins → a little over 3 per bar
          ctx.createMediaStreamSource(media).connect(analyser)
          const bins = new Uint8Array(analyser.frequencyBinCount)
          const perBar = Math.max(1, Math.floor(bins.length / WAVEFORM_BARS))
          const read = () => {
            analyser.getByteFrequencyData(bins)
            const next: number[] = []
            for (let bar = 0; bar < WAVEFORM_BARS; bar++) {
              let sum = 0
              for (let i = 0; i < perBar; i++) sum += bins[bar * perBar + i] ?? 0
              next.push(Math.min(1, sum / perBar / 200))
            }
            setLevels(next)
            frame.current = requestAnimationFrame(read)
          }
          frame.current = requestAnimationFrame(read)
        } catch {
          // No AudioContext is not a reason to lose a recording. The bars stay at
          // rest and the timer — the part that matters — keeps running.
        }
      })
      .catch((error: unknown) => {
        setPhase('failed')
        const name = error instanceof Error ? error.name : ''
        setProblem(
          name === 'NotAllowedError' || name === 'SecurityError'
            ? `The browser blocked the microphone, so nothing was recorded. Allow it for this site, then ${retry.current}.`
            : name === 'NotFoundError' || name === 'NotReadableError'
              ? 'No microphone was available, so nothing was recorded.'
              : 'The microphone could not be opened, so nothing was recorded.',
        )
      })
  }, [stopWith])

  return {
    phase,
    elapsed,
    levels,
    problem,
    start,
    stopAndSave: useCallback(() => stopWith('save'), [stopWith]),
    discard: useCallback(() => stopWith('discard'), [stopWith]),
    reset: useCallback(() => {
      setPhase('idle')
      setProblem(null)
      setElapsed(0)
    }, []),
  }
}

/** '0:47' — the 62px timer. */
export function timerLabel(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m}:${String(s).padStart(2, '0')}`
}
